/**
 * English catalogue — the source of truth for the message key set.
 *
 * Keys ending in `_one` are the singular form; the plural key is used for
 * every other count. See `translate()`.
 */
export const en = {
  // --- Navigation ---------------------------------------------------------
  'nav.collection': 'Collection',
  'nav.insights': 'Insights',
  'nav.add': 'Add',
  'nav.wishlist': 'Wishlist',
  'nav.settings': 'Settings',
  'nav.skipToContent': 'Skip to content',

  // --- Shared -------------------------------------------------------------
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.done': 'Done',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.remove': 'Remove',
  'common.add': 'Add',
  'common.search': 'Search',
  'common.clear': 'Clear',
  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.optional': 'optional',
  'common.none': 'None',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.signOut': 'Sign out',
  'common.apply': 'Apply',
  'common.reset': 'Reset',
  'common.more': 'More',

  // --- Disc formats -------------------------------------------------------
  'format.4k': '4K UHD',
  'format.hd': 'Full HD',
  'format.combo': '4K + Full HD',
  'format.4k.short': '4K',
  'format.hd.short': 'HD',
  'format.combo.short': '4K+HD',
  'format.combo.hint': 'One case, two discs',
  'format.4k.hint': 'A single UHD disc',
  'format.hd.hint': 'A single Blu-ray disc',
  'format.label': 'Format',

  // --- Sign in ------------------------------------------------------------
  'auth.signIn.title': 'Physical Movies',
  'auth.signIn.tagline': 'Every disc on your shelf, and every time you played it.',
  'auth.signIn.button': 'Continue with Google',
  'auth.signIn.busy': 'Opening Google…',
  'auth.signIn.error': 'Sign-in did not complete. Try again.',
  'auth.signIn.popupBlocked':
    'Your browser blocked the sign-in window. Allow pop-ups and try again.',
  'auth.signIn.errorCode': 'Error code: {code}',
  'auth.locked.title': 'This collection is private',
  'auth.locked.body':
    'You are signed in as {email}, but this instance only opens for its owner. Nothing here is readable until an owner adds your account.',
  'auth.locked.ownerTitle': 'Is this your instance?',
  'auth.locked.ownerBody': 'Run this from the project folder, then reload the page:',
  'auth.locked.uidLabel': 'Your user ID',
  'auth.locked.copyUid': 'Copy user ID',
  'auth.loading': 'Checking your access…',

  // --- Collection ---------------------------------------------------------
  'collection.title': 'Collection',
  'collection.summary_one': '{count} movie · {discs} discs',
  'collection.summary': '{count} movies · {discs} discs',
  'collection.searchPlaceholder': 'Search titles, directors, editions',
  'collection.searchLabel': 'Search the collection',
  'collection.empty.title': 'Nothing on the shelf yet',
  'collection.empty.body': 'Add your first disc and the wall starts filling up.',
  'collection.empty.action': 'Add a movie',
  'collection.noResults.title': 'No matches',
  'collection.noResults.body': 'Try a different search, or clear the filters.',
  'collection.clearFilters': 'Clear filters',
  'collection.filters': 'Filters',
  'collection.filtersActive': 'Filters ({count})',
  'collection.sort': 'Sort',
  'collection.surprise': 'Surprise me',
  'collection.results_one': '{count} result',
  'collection.results': '{count} results',
  'collection.lentOutBanner_one': '{count} copy is out on loan',
  'collection.lentOutBanner': '{count} copies are out on loan',

  'filter.title': 'Filters',
  'filter.format': 'Format',
  'filter.watchState': 'Played',
  'filter.loanState': 'Availability',
  'filter.genres': 'Genres',
  'filter.any': 'Any',
  'filter.watched': 'Played',
  'filter.unwatched': 'Never played',
  'filter.loaned': 'Lent out',
  'filter.onShelf': 'On the shelf',

  'sort.title': 'Title',
  'sort.added': 'Recently added',
  'sort.releaseDate': 'Release date',
  'sort.lastWatched': 'Last played',
  'sort.watchCount': 'Times played',
  'sort.runtime': 'Runtime',
  'sort.ascending': 'Ascending',
  'sort.descending': 'Descending',

  // --- Movie detail -------------------------------------------------------
  'movie.logWatch': 'Log watch',
  'movie.logWatchDetails': 'Add details',
  'movie.copies': 'Copies',
  'movie.copies_one': 'Copy',
  'movie.addCopy': 'Add a copy',
  'movie.watchHistory': 'Watch history',
  'movie.neverWatched': 'Never played',
  'movie.watchedTimes_one': 'Played once',
  'movie.watchedTimes': 'Played {count} times',
  'movie.lastWatched': 'Last played {date}',
  'movie.directedBy': 'Directed by {names}',
  'movie.overview': 'Synopsis',
  'movie.note': 'Your note',
  'movie.notePlaceholder': 'Where you bought it, who gave it to you, anything.',
  'movie.delete': 'Delete movie',
  'movie.deleteConfirm.title': 'Delete {title}?',
  'movie.deleteConfirm.body_one':
    'This removes the movie, its copies and 1 watch entry. It cannot be undone.',
  'movie.deleteConfirm.body':
    'This removes the movie, its copies and {count} watch entries. It cannot be undone.',
  'movie.deleted': '{title} deleted',
  'movie.refresh': 'Refresh from TMDB',
  'movie.refreshed': 'Details refreshed',
  'movie.notFound.title': 'Movie not found',
  'movie.notFound.body': 'It may have been deleted on another device.',
  'movie.backToCollection': 'Back to collection',
  'movie.unavailable': 'Every copy is lent out',
  'movie.runtimeUnknown': 'Runtime unknown',
  'movie.addRuntime': 'Add a runtime to count this towards watch time.',

  // --- Copies -------------------------------------------------------------
  'copy.title': 'Copy',
  'copy.edition': 'Edition',
  'copy.editionPlaceholder': 'Steelbook, Mediabook, Director’s Cut…',
  'copy.purchasedAt': 'Bought on',
  'copy.notes': 'Notes',
  'copy.notesPlaceholder': 'Slipcover, region, box set…',
  'copy.add': 'Add copy',
  'copy.added': 'Copy added',
  'copy.updated': 'Copy updated',
  'copy.remove': 'Remove copy',
  'copy.removed': 'Copy removed',
  'copy.removeConfirm': 'Remove this copy from the shelf?',
  'copy.discs_one': '1 disc',
  'copy.discs': '{count} discs',
  'copy.lendOut': 'Lend out',
  'copy.lending': 'Lend this copy',
  'copy.borrower': 'Who has it',
  'copy.borrowerPlaceholder': 'A name',
  'copy.lentTo': 'With {name}',
  'copy.lentSince': 'since {date}',
  'copy.since': 'Since',
  'copy.markReturned': 'Mark returned',
  'copy.returned': 'Back on the shelf',
  'copy.lent': 'Marked as lent out',
  'copy.lentNeedsName': 'Add a name so you know where it went.',

  // --- Watch logging ------------------------------------------------------
  'watch.title': 'Log a watch',
  'watch.when': 'When',
  'watch.now': 'Just now',
  'watch.rating': 'Rating',
  'watch.ratingValue': '{count} of 5',
  'watch.clearRating': 'Clear rating',
  'watch.note': 'Note',
  'watch.notePlaceholder': 'Anything worth remembering',
  'watch.submit': 'Log watch',
  'watch.logged': 'Watch logged',
  'watch.deleteEntry': 'Delete entry',
  'watch.deleted': 'Watch entry deleted',
  'watch.deleteConfirm': 'Delete this watch entry?',
  'watch.futureDate': 'Pick a date that has already happened.',
  'watch.empty': 'No watches logged yet.',

  // --- Adding a movie -----------------------------------------------------
  'filter.location': 'Location',

  // --- Locations -------------------------------------------------------------
  'settings.locations': 'Locations',
  'settings.locations.hint':
    'Where the discs actually are, so you can tell what is within reach tonight.',
  'settings.locations.empty':
    'No locations yet. Add the first one and every disc you already own counts as being there.',
  'settings.locations.add': 'Add a location',
  'settings.locations.namePlaceholder': 'Living room',
  'settings.locations.save': 'Add',
  'settings.locations.default': 'Default',
  'settings.locations.makeDefault': 'Make this the default',
  'settings.locations.defaultHint':
    'A disc that does not say where it is counts as being here — which is every disc added before you set this up.',
  'settings.locations.rename': 'Rename {name}',
  'settings.locations.remove': 'Delete {name}',
  'settings.locations.added': '{name} added',
  'settings.locations.removed': '{name} deleted',
  'settings.locations.deleteTitle': 'Delete {name}?',
  'settings.locations.deleteBody':
    'The discs kept there are not deleted — they fall back to the default location.',
  'settings.locations.deleteAction': 'Delete the location',
  'copy.location': 'Where it is',

  // --- Printing --------------------------------------------------------------
  'collection.print': 'Print or save as PDF',
  'print.title': 'Collection',
  'print.subtitle': '{count} films · {discs} discs',
  'print.action': 'Print',
  'print.back': 'Back',
  'print.preparing': 'Loading the covers…',
  'print.preparingCount': 'Loading the covers… {loaded} of {total}',
  'print.printing': 'Preparing the pages…',
  'print.hint': 'Print, or choose "Save as PDF" in the print dialogue.',
  'print.filtered': 'Filtered: {summary}',
  'print.empty': 'Nothing matches the current filters.',
  'add.title': 'Add a movie',
  'add.searchPlaceholder': 'Search for a title',
  'add.searchHint': 'Results come from TMDB.',
  'add.searching': 'Searching…',
  'add.noResults': 'No results on TMDB.',
  'add.trending.title': 'Trending this week',
  'add.trending.hint':
    'Films that played in cinemas. Series and streaming-only releases are left out.',
  'add.trending.loading': 'Looking up what is trending…',
  'add.trending.failed': 'Could not load what is trending.',
  'add.trending.empty': 'Nothing trending has had a cinema release this week.',
  'add.manual': 'Add by hand instead',
  'add.manualTitle': 'Add by hand',
  'add.field.title': 'Title',
  'add.field.titleDe': 'German title',
  'add.field.year': 'Year',
  'add.field.runtime': 'Runtime in minutes',
  'add.field.director': 'Director',
  'add.titleRequired': 'A title is required.',
  'add.chooseFormat': 'What is in the case?',
  'add.confirm': 'Add to collection',
  'add.added': '{title} added',
  'add.alreadyOwned': 'Already on your shelf',
  'add.addAnotherCopy': 'Add another copy',
  'add.openExisting': 'Open it',
  'add.tmdbDisabled':
    'TMDB lookup is switched off. Set VITE_TMDB_READ_TOKEN in .env.local to search, or add movies by hand.',
  'add.toWishlist': 'Put on wishlist',
  'add.loadFailed': 'Could not load that title from TMDB.',

  // --- Insights -----------------------------------------------------------
  'insights.title': 'Insights',
  'insights.tonight': 'Pick for tonight',
  'insights.shelf': 'Your shelf',
  'insights.shelfHint': 'Every disc, by format.',
  'insights.watchTime': 'Watch time',
  'insights.perMonth': 'Watches per month',
  'insights.topMovies': 'Most played',
  'insights.ratings': 'Your ratings',
  'insights.genres': 'Genres',
  'insights.decades': 'Decades',
  'insights.empty.title': 'Nothing to measure yet',
  'insights.empty.body': 'Log a watch and the numbers start here.',
  'insights.stat.movies': 'Movies',
  'insights.stat.discs': 'Discs',
  'insights.stat.copies': 'Cases',
  'insights.stat.watches': 'Watches',
  'insights.stat.unwatched': 'Never played',
  'insights.stat.avgRating': 'Average rating',
  'insights.stat.lentOut': 'Lent out',
  'insights.stat.totalTime': 'All time',
  'insights.stat.thisYear': 'This year',
  'insights.watchedShare': '{percent} of the shelf has been played',
  'insights.busiestMonth': 'Busiest month: {month}',
  'insights.noRatings': 'No ratings yet.',
  'insights.starsAxis': '{count}★',
  'insights.moviesCounted': '{count} movies',
  'insights.watchesCounted': '{count} watches',

  // --- Recommendations ----------------------------------------------------
  'rec.title': 'Pick for tonight',
  'rec.neverWatched': 'Never played',
  'rec.longAgo': 'Last played {days} days ago',
  'rec.ownedFor': 'On the shelf for {days} days',
  'rec.empty.title': 'Nothing is gathering dust',
  'rec.empty.body': 'Everything on the shelf has been played recently. Good problem to have.',
  'rec.another': 'Show another',
  'rec.seeAll': 'See all {count}',
  'rec.listTitle': 'Gathering dust',

  // --- Wishlist -----------------------------------------------------------
  'wishlist.title': 'Wishlist',
  'wishlist.empty.title': 'Nothing on the list',
  'wishlist.empty.body': 'Keep track of the discs you are still hunting for.',
  'wishlist.add': 'Add to wishlist',
  'wishlist.added': '{title} added to the wishlist',
  'wishlist.desiredFormat': 'Format you want',
  'wishlist.remove': 'Remove from wishlist',
  'wishlist.removed': 'Removed from the wishlist',
  'wishlist.bought': 'I bought it',
  'wishlist.moved': '{title} moved to the collection',
  'wishlist.count_one': '{count} title',
  'wishlist.count': '{count} titles',
  'wishlist.note': 'Note',
  'wishlist.notePlaceholder': 'Which release, what price, where.',

  // --- Wishlist filters and trash -----------------------------------------
  'wishlist.filters': 'Filters',
  'wishlist.buyable': 'Availability',
  'wishlist.buyable.all': 'Any',
  'wishlist.buyable.buyable': 'Out now',
  'wishlist.buyable.upcoming': 'Not out yet',
  'wishlist.desiredFormatFilter': 'Format wanted',
  'wishlist.markBuyable': 'Mark as out now',
  'wishlist.markUpcoming': 'Mark as not out yet',
  'wishlist.markedBuyable': 'Marked as out now',
  'wishlist.markedUpcoming': 'Marked as not out yet',
  'wishlist.upcomingBadge': 'Not out yet',
  'wishlist.results_one': '{count} title',
  'wishlist.results': '{count} titles',
  'wishlist.noResults.title': 'No matches',
  'wishlist.noResults.body': 'Try a different search, or clear the filters.',
  'wishlist.sort.title': 'Title',
  'wishlist.sort.added': 'Recently added',
  'wishlist.sort.releaseDate': 'Release date',
  'wishlist.sort.runtime': 'Runtime',
  'wishlist.deleteConfirm.title': 'Remove {title}?',
  'wishlist.deleteConfirm.body':
    'It goes to the trash and stays there for {days} days, so you can still get it back.',
  'wishlist.deleteConfirm.action': 'Move to trash',
  'wishlist.trash': 'Trash',
  'wishlist.trash.open': 'Trash ({count})',
  'wishlist.trash.back': 'Back to the wishlist',
  'wishlist.trash.empty.title': 'The trash is empty',
  'wishlist.trash.empty.body': 'Anything you remove waits here for {days} days first.',
  'wishlist.trash.daysLeft_one': '1 day left',
  'wishlist.trash.daysLeft': '{count} days left',
  'wishlist.trash.restore': 'Put back',
  'wishlist.trash.restored': '{title} is back on the wishlist',
  'wishlist.trash.deleteForever': 'Delete for good',
  'wishlist.trash.deleteForeverConfirm.title': 'Delete {title} for good?',
  'wishlist.trash.deleteForeverConfirm.body': 'This cannot be undone.',
  'wishlist.trash.deletedForever': 'Deleted for good',
  'wishlist.movedToTrash': '{title} moved to the trash',

  // --- Settings -----------------------------------------------------------
  'settings.title': 'Settings',
  'settings.appearance': 'Appearance',
  'settings.theme': 'Theme',
  'settings.theme.system': 'Match system',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',
  'settings.language': 'Language',
  'settings.language.system': 'Match system',
  'settings.language.en': 'English',
  'settings.language.de': 'Deutsch',
  'settings.data': 'Your data',
  'settings.export': 'Export collection',
  'settings.exportHint':
    'Downloads one JSON file with every movie, copy, watch and wishlist entry.',
  'settings.exported': 'Export downloaded',
  'settings.import': 'Import a file',
  'settings.importHint':
    'Merges an exported file into this collection. Entries that already exist are updated, not duplicated.',
  'settings.importing': 'Importing…',
  'settings.imported':
    '{movies} movies, {watches} watches and {wishlist} wishlist entries imported.',
  'settings.importFailed': 'That file could not be read: {reason}',
  'settings.account': 'Account',
  'settings.signedInAs': 'Signed in as {email}',
  'settings.about': 'About',
  'settings.version': 'Version',
  'settings.source': 'Source code',
  'settings.tmdbCredit': 'Movie data and artwork from TMDB. This product is not endorsed by TMDB.',

  // --- Errors and notices -------------------------------------------------
  'error.generic': 'Something went wrong. Try again.',
  'error.permission': 'Your account is not allowed to change this collection.',
  'error.offline': 'No connection to Firestore. Changes will not be saved.',
  'error.notConfigured.title': 'Firebase is not configured',
  'error.notConfigured.body':
    'Copy .env.example to .env.local and fill in your Firebase web config, then restart the dev server.',
  'error.missingKeys': 'Still missing',

  // --- Units --------------------------------------------------------------
  'unit.minutesShort': '{count} min',
  'unit.hoursShort': '{count} h',
  'unit.hoursMinutes': '{hours} h {minutes} min',
  'unit.days_one': '1 day',
  'unit.days': '{count} days',
  'unit.never': 'never',
} as const;

export type MessageKey = keyof typeof en;
